import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatPaginator,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { CommonService } from '@equine-ui-nx/account';
import { ShowTypePipe } from '../../pipe/show-type.pipe';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'eq-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    ShowTypePipe,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './eq-table.component.html',
  styleUrl: './eq-table.component.scss',
})
export class EqTableComponent implements OnInit, AfterViewInit {
  @Input() dataResource: any[] = [];
  @Input() dynamicColumns: any[] = [];
  @Input() selectedItems: any[] = [];
  @Input() totalItemCount: number = 10;
  @Input() checkbox: boolean = false;
  @Input() pagination: boolean = false;
  @Input() isCustomizedTable: boolean = false;
  @Input() themeClass: string = "defaultClass";
  pageSize: number = 10;
  pageIndex: number = 0;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Output() action = new EventEmitter<{ row: any; action: any }>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() sortEvent = new EventEmitter<any>();
  selection = new SelectionModel<any>(true, []);
  displayedColumns: string[] = [];
  dataSource!: MatTableDataSource<any>;
  resizing = false;
  resizingColumn: string = '';
  startX: number = 0;
  startWidth: number = 0;
  columnWidths: { [key: string]: number } = {};

  constructor(
    public readonly commonService: CommonService,
    private readonly renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.dataSource = new MatTableDataSource<any>(this.dataResource);
    if (this.checkbox) {
      this.displayedColumns = [
        'select',
        ...this.dynamicColumns.map((c: any) => c.columnDef),
      ];
      if (changes['dataResource']?.currentValue) {
        this.dataSource = new MatTableDataSource<any>(this.dataResource);
        this.ngAfterViewInit();
      }
      if (changes['selectedItems']?.currentValue) {
        this.selectedItems = changes['selectedItems']?.currentValue;
        this.ngAfterViewInit();
      }
    } else {
      this.displayedColumns = [
        ...this.dynamicColumns.map((c: any) => c.columnDef),
      ];
    }
  }
  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<any>(this.dataResource);
    this.selection.changed.subscribe((res) => {
      this.onSelect.emit(this.selection.selected);
    });
  }
  ngAfterViewInit(): void {
    if (this.selectedItems !== undefined && this.selectedItems?.length > 0) {
      const selectedIds = new Set(
        this.selectedItems.map((item) => item?._id || item)
      );
      this.selection.clear();
      this.dataSource.data.forEach((item) => {
        if (selectedIds.has(item._id)) {
          this.selection.select(item);
        }
      });
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
  }
  
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataResource);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataResource.length;
    return numSelected === numRows;
  }

  checkboxLabel() {
    return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
  }

  getColumnData(row: any, column: any): any {
    return typeof column.cell === 'function'
      ? column.cell(row)
      : row[column.columnDef] || '';
  }

  dispatchAction(row: any, action: any) {
    this.action.emit({ row, action });
  }
  handlePagChange(page: any) {
    this.pageIndex = page.pageIndex;
    this.pageSize = page.pageSize;
    this.pageChange.emit(page);
  }

  onSort(sortState: Sort): void {
    const column = this.dynamicColumns.find(column => column.columnDef === sortState.active);
    this.sortEvent.emit({ sort: sortState.direction, sortBy: column?.sortableKey });
  }

  startResize(event: MouseEvent, column: string): void {
    event.preventDefault();
    this.resizing = true;
    this.resizingColumn = column;
    this.startX = event.pageX;
    this.startWidth = this.columnWidths[column] || 150;
    this.renderer.addClass(document.body, 'resizing');
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.resizing) return;

    const deltaX = event.pageX - this.startX;
    const newWidth = this.startWidth + deltaX;

    if (newWidth > 50) {
      this.columnWidths[this.resizingColumn] = newWidth;
    }
  }

  @HostListener('document:mouseup')
  stopResize(): void {
    if (this.resizing) {
      this.resizing = false;
      this.renderer.removeClass(document.body, 'resizing');
    }
  }

  getColumnWidth(column: string): number {
    return this.columnWidths[column] || 220;
  }
}
